import { useCallback, useEffect, useRef, useState } from "react";
import { auth } from "@/lib/picasso-firebase"
import axios from "axios";

interface UseTranscriptionReturn {
    transcription: Transcription | null;
    setTranscription: (transcription: Transcription) => void;
    fetchTranscription: (video: File) => Promise<void>;
    saveTranscription: (transcription: Transcription) => Promise<void>;
    deleteTranscription: (transcriptionId: string) => Promise<void>;
    isLoading: boolean;
    handleFileSelection: (file: File) => void;
    previewUrl: string | null;
    isTranscription: boolean;
    file: File | null;
    setFile: (file: File) => void;
    fileType: string | null;
    handleRemoveFile: () => void;
    transcriptions: Transcription[];
    isLoadingTranscriptions: boolean;
    isSaving: boolean;
    currentOffset: number;
    hasMore: boolean;
    loadMoreTranscriptions: () => Promise<void>;
    isLoadingMore: boolean;
    totalCount: number;
    totalUnfilteredCount: number;
    reloadTranscriptions: () => Promise<void>;
    addTranscription: (transcription: Transcription) => void;
}

export interface Transcription {
    title: string;
    transcription: string;
    user_email: string;
    created_at: string;
    id?: string;
}

export const useTranscription = (): UseTranscriptionReturn => {
    const [transcription, setTranscription] = useState<Transcription | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isTranscription, setIsTranscription] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [fileType, setFileType] = useState<string | null>(null);
    const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
    const [isLoadingTranscriptions, setIsLoadingTranscriptions] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [totalUnfilteredCount, setTotalUnfilteredCount] = useState(0);

    // Use ref to track offset without causing dependency re-renders
    const offsetRef = useRef(0);

    const limit = 12;
    const getTranscriptions = useCallback(async () => {
        const currentUser = auth.currentUser
        if (!currentUser) {
            throw new Error("Authentication required")
        }
        const token = await currentUser.getIdToken()
        setIsLoadingTranscriptions(true);
        try {
            const response = await axios.get(`${import.meta.env.VITE_BASE_URL}/picasso/transcription/get-transcriptions?limit=${limit}&offset=${offsetRef.current}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const newTranscriptions = response.data.transcriptions;
            setTranscriptions(prev => {
                const existingIds = new Set(prev.map((t: Transcription) => t.created_at));
                const uniqueNewTranscriptions = newTranscriptions.filter((t: Transcription) => !existingIds.has(t.created_at));
                return [...prev, ...uniqueNewTranscriptions];
            });
            offsetRef.current = offsetRef.current + newTranscriptions.length;
            setCurrentOffset(offsetRef.current);
            setHasMore(response.data.has_more);
            setTotalCount(response.data.total_count || 0);
            setTotalUnfilteredCount(response.data.total_unfiltered_count || response.data.total_count || 0);
        } catch (error) {
            console.error("Error fetching transcriptions:", error);
        } finally {
            setIsLoadingTranscriptions(false);
            setIsLoadingMore(false);

        }
    }, []);

    const reloadTranscriptions = useCallback(async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            throw new Error("Authentication required");
        }
        const token = await currentUser.getIdToken();
        setIsLoadingTranscriptions(true);
        try {
            const response = await axios.get(`${import.meta.env.VITE_BASE_URL}/picasso/transcription/get-transcriptions?limit=${limit}&offset=0`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const newTranscriptions = response.data.transcriptions;
            setTranscriptions(newTranscriptions);
            offsetRef.current = newTranscriptions.length;
            setCurrentOffset(newTranscriptions.length);
            setHasMore(response.data.has_more);
            setTotalCount(response.data.total_count || 0);
            setTotalUnfilteredCount(response.data.total_unfiltered_count || response.data.total_count || 0);
        } catch (error) {
            console.error("Error reloading transcriptions:", error);
        } finally {
            setIsLoadingTranscriptions(false);
            setIsLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        if (transcriptions.length === 0) {
            getTranscriptions();
        }
    }, []);

    const fetchTranscription = useCallback(async (video: File) => {
        const formData = new FormData();
        formData.append("video", video);
        setIsLoading(true);
        try {
            const currentUser = auth.currentUser
            if (!currentUser) {
                throw new Error("Authentication required")
            }

            const token = await currentUser.getIdToken()
            const response = await axios.post(`${import.meta.env.VITE_BASE_URL}/picasso/transcription/transcribe`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setIsTranscription(true);
            const newTranscription = response.data;

            setTranscription(newTranscription);

            // Directly add the transcription to the list
            setTranscriptions(prev => [newTranscription, ...prev]);
            setTotalCount(prev => prev + 1);
            setTotalUnfilteredCount(prev => prev + 1);

            // Clear the uploaded file and preview after successful transcription
            setFile(null);
            setFileType(null);
            setPreviewUrl(null);
        } catch (error: unknown) {
            console.error("Error fetching transcription:", error);
            const axiosError = error as { response?: { data?: { detail?: string } } };
            throw new Error(axiosError.response?.data?.detail || "Transcription failed");
        } finally {
            setIsLoading(false);
        }
    }, []);

    const saveTranscription = useCallback(async (_transcription: Transcription) => {
        // No longer makes API call - transcription is already saved when generated
        // Just clear the form state
        setIsSaving(true);
        try {
            // Transcription was already added to the list in fetchTranscription
            // Just clean up the form
            await Promise.resolve();
        } catch (error) {
            console.error("Error saving transcription:", error);
        } finally {
            setIsSaving(false);
            setTranscription(null);
            setIsTranscription(false);
            setFile(null);
            setFileType(null);
            setPreviewUrl(null);
        }
    }, []);

    const deleteTranscription = useCallback(async (transcriptionId: string) => {
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) {
                throw new Error("Authentication required");
            }

            const token = await currentUser.getIdToken();
            await axios.delete(
                `${import.meta.env.VITE_BASE_URL}/picasso/transcription/delete-transcription/${transcriptionId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            // Remove from local state
            setTranscriptions(prev => prev.filter(t => t.id !== transcriptionId));
            setTotalCount(prev => prev - 1);
            setTotalUnfilteredCount(prev => prev - 1);
        } catch (error) {
            console.error("Error deleting transcription:", error);
            throw error;
        }
    }, []);

    const handleFileSelection = useCallback((file: File) => {
        setIsTranscription(false);
        const reader = new FileReader();
        reader.onload = (e) => {
            setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);
        setFile(file);
        setFileType(file.type);
    }, []); // No dependencies needed - setState functions are stable

    const handleRemoveFile = useCallback(() => {
        setPreviewUrl(null);
        setFile(null);
        setFileType(null);
        setIsTranscription(false);
        setTranscription(null);
        setTranscription(null);
    }, []); // No dependencies needed - setState functions are stable

    const loadMoreTranscriptions = useCallback(async () => {
        if (isLoadingMore || !hasMore) return;

        setIsLoadingMore(true);
        await getTranscriptions();
    }, [getTranscriptions, isLoadingMore, hasMore]);

    const addTranscription = useCallback((newTranscription: Transcription) => {
        setTranscriptions(prev => [newTranscription, ...prev]);
        setTotalCount(prev => prev + 1);
        setTotalUnfilteredCount(prev => prev + 1);
    }, []);

    return {
        transcription,
        setTranscription,
        fetchTranscription,
        isLoading,
        saveTranscription,
        deleteTranscription,
        handleFileSelection,
        previewUrl,
        isTranscription,
        file,
        setFile,
        fileType,
        handleRemoveFile,
        transcriptions,
        isLoadingTranscriptions,
        isSaving,
        currentOffset,
        hasMore,
        loadMoreTranscriptions,
        isLoadingMore,
        totalCount,
        totalUnfilteredCount,
        reloadTranscriptions,
        addTranscription
    }
}
